// src/services/PresenceService.js
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, onSnapshot, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const PRESENCE_COLLECTION = 'presence';
const TYPING_COLLECTION = 'typing';

class PresenceService {
    constructor() {
        this.userId = null;
        this.userEmail = null;
        this.userName = null;
        this.heartbeatInterval = null;
        this.unsubscribePresence = null;
        this.unsubscribeTyping = null;
        this.typingTimeouts = {};
        this.presenceListeners = [];
        this.typingListeners = [];
        this.isInitialized = false;
    }

    /**
     * Initialize presence tracking for a user
     */
    async initialize(user) {
        if (!user) {
            console.warn('[Presence] Cannot initialize: No user provided');
            return;
        }

        if (this.isInitialized && this.userId === user.uid) {
            return;
        }

        // Clean up previous instance if any
        if (this.isInitialized) {
            await this.cleanup();
        }

        this.userId = user.uid;
        this.userEmail = user.email;
        this.userName = user.displayName || user.nome || user.email?.split('@')[0] || 'Unknown';
        this.isInitialized = true;

        // Set user as online
        await this.updatePresence(true);

        // Start heartbeat to keep presence alive
        this.startHeartbeat();

        // Set up cleanup on page unload
        window.addEventListener('beforeunload', () => this.cleanup());
        window.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    }

    /**
     * Update presence status in Firestore
     */
    async updatePresence(online, customData = {}) {
        if (!this.userId) {
            console.warn('[Presence] Cannot update presence: No user ID');
            return;
        }

        try {
            const presenceRef = doc(db, PRESENCE_COLLECTION, this.userId);

            const presenceData = {
                userId: this.userId,
                email: this.userEmail,
                displayName: this.userName,
                online: online,
                lastSeen: Timestamp.now(),
                lastActivity: Timestamp.now(),
                updatedAt: Timestamp.now(),
                status: online ? 'online' : 'offline',
                ...customData
            };

            // Check if document exists
            const docSnap = await getDoc(presenceRef);

            if (docSnap.exists()) {
                await updateDoc(presenceRef, presenceData);
            } else {
                // Add additional fields for new document
                await setDoc(presenceRef, {
                    ...presenceData,
                    createdAt: Timestamp.now(),
                    firstSeen: Timestamp.now()
                });
            }
        } catch (error) {
            console.debug('[Presence] Firestore update failed:', error.message);
        }
    }

    /**
     * Start heartbeat to keep presence alive
     */
    startHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }

        // Update presence every 30 seconds
        this.heartbeatInterval = setInterval(async () => {
            if (this.userId && this.isInitialized) {
                await this.updatePresence(true, {
                    lastActivity: Timestamp.now()
                });
            }
        }, 30000);
    }

    /**
     * Handle visibility change (tab hidden/focused)
     */
    async handleVisibilityChange() {
        if (!this.userId || !this.isInitialized) return;

        if (document.hidden) {
            // Tab hidden - user might be away
            await this.updatePresence(true, {
                status: 'away',
                lastActivity: Timestamp.now()
            });
        } else {
            // Tab visible - user is back
            await this.updatePresence(true, {
                status: 'online',
                lastActivity: Timestamp.now()
            });
        }
    }

    /**
     * Set typing status for a channel
     */
    async setTyping(channelId, isTyping, userId = null) {
        const uid = userId || this.userId;
        if (!uid || !channelId) return;

        try {
            const typingRef = doc(db, TYPING_COLLECTION, channelId, 'users', uid);

            if (isTyping) {
                await setDoc(typingRef, {
                    uid: uid,
                    displayName: this.userName,
                    timestamp: Timestamp.now(),
                    channelId: channelId
                });

                // Clear typing after 3 seconds of inactivity
                if (this.typingTimeouts[channelId]) {
                    clearTimeout(this.typingTimeouts[channelId]);
                }
                this.typingTimeouts[channelId] = setTimeout(async () => {
                    await this.clearTyping(channelId, uid);
                }, 3000);
            } else {
                await this.clearTyping(channelId, uid);
            }
        } catch (error) {
            console.debug('[Presence] Typing update failed:', error.message);
        }
    }

    /**
     * Clear typing status for a user in a channel
     */
    async clearTyping(channelId, userId) {
        if (!channelId || !userId) return;

        try {
            const typingRef = doc(db, TYPING_COLLECTION, channelId, 'users', userId);
            await deleteDoc(typingRef);
        } catch (error) {
            console.debug('[Presence] Clear typing failed:', error.message);
        }
    }

    /**
     * Subscribe to typing indicators for a channel
     */
    subscribeToTyping(channelId, callback) {
        if (!channelId) {
            console.warn('[Presence] Cannot subscribe to typing: No channel ID');
            return () => { };
        }

        const typingRef = collection(db, TYPING_COLLECTION, channelId, 'users');

        const unsubscribe = onSnapshot(typingRef, (snapshot) => {
            const typingUsers = [];
            const now = Date.now();

            snapshot.forEach((doc) => {
                const data = doc.data();
                const timestamp = data.timestamp?.toDate?.() || new Date();

                // Only show if typing within last 5 seconds
                if ((now - timestamp.getTime()) < 5000) {
                    typingUsers.push({
                        uid: data.uid,
                        displayName: data.displayName || 'User',
                        timestamp: timestamp
                    });
                } else {
                    // Clean up stale typing indicators
                    this.clearTyping(channelId, data.uid);
                }
            });

            callback(typingUsers || []);
        }, (error) => {
            console.error('[Presence] Typing subscription error:', error);
            callback([]);
        });

        this.typingListeners.push(unsubscribe);
        return unsubscribe;
    }

    /**
     * Unsubscribe from typing indicators
     */
    unsubscribeFromTyping(unsubscribe) {
        if (unsubscribe) {
            unsubscribe();
            const index = this.typingListeners.indexOf(unsubscribe);
            if (index > -1) {
                this.typingListeners.splice(index, 1);
            }
        }
    }

    /**
     * Clean up presence data
     */
    async cleanup() {

        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }

        // Clear all typing timeouts
        Object.keys(this.typingTimeouts).forEach(key => {
            clearTimeout(this.typingTimeouts[key]);
        });
        this.typingTimeouts = {};

        // Unsubscribe from all listeners
        this.presenceListeners.forEach(unsub => {
            try { unsub(); } catch (e) { }
        });
        this.presenceListeners = [];

        this.typingListeners.forEach(unsub => {
            try { unsub(); } catch (e) { }
        });
        this.typingListeners = [];

        if (this.userId && this.isInitialized) {
            try {
                // Mark user as offline
                await this.updatePresence(false);
            } catch (error) {
                console.debug('[Presence] Cleanup error:', error.message);
            }
        }

        this.isInitialized = false;
        this.userId = null;
        this.userEmail = null;
        this.userName = null;
    }

    /**
     * Subscribe to online users (real-time updates)
     */
    subscribeToOnlineUsers(callback) {
        const q = query(
            collection(db, PRESENCE_COLLECTION),
            where('online', '==', true)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const onlineUsers = [];
            const now = Date.now();

            snapshot.forEach((doc) => {
                const data = doc.data();
                const lastSeen = data.lastSeen?.toDate?.() || new Date(0);
                // Check if user is truly online (within 2 minutes)
                const isOnline = data.online === true && (now - lastSeen.getTime()) < 120000;

                // Only include users that are actually online
                if (isOnline) {
                    onlineUsers.push({
                        id: doc.id,
                        ...data,
                        online: true
                    });
                }
            });

            // Sort by display name
            onlineUsers.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));

            callback(onlineUsers);
        }, (error) => {
            console.error('[Presence] Subscription error:', error);
            callback([]);
        });

        this.presenceListeners.push(unsubscribe);
        return unsubscribe;
    }

    /**
     * Get all users with presence status (one-time fetch)
     */
    async getUsersWithPresence() {
        try {
            const q = query(
                collection(db, PRESENCE_COLLECTION),
                where('online', '==', true)
            );

            const snapshot = await getDocs(q);
            const usersWithPresence = {};
            const now = Date.now();

            snapshot.forEach((doc) => {
                const data = doc.data();
                const lastSeen = data.lastSeen?.toDate?.() || new Date(0);
                const isOnline = data.online === true && (now - lastSeen.getTime()) < 120000;

                usersWithPresence[doc.id] = {
                    id: doc.id,
                    ...data,
                    online: isOnline
                };
            });

            return usersWithPresence;
        } catch (error) {
            console.error('[Presence] Error fetching users:', error);
            return {};
        }
    }

    /**
     * Get single user's presence status
     */
    async getUserPresence(userId) {
        try {
            const presenceRef = doc(db, PRESENCE_COLLECTION, userId);
            const docSnap = await getDoc(presenceRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const lastSeen = data.lastSeen?.toDate?.() || new Date(0);
                const isOnline = data.online === true && (Date.now() - lastSeen.getTime()) < 120000;

                return {
                    id: docSnap.id,
                    ...data,
                    online: isOnline
                };
            }
            return null;
        } catch (error) {
            console.error('[Presence] Error fetching user presence:', error);
            return null;
        }
    }

    /**
     * Check if a user is online
     */
    async isUserOnline(userId) {
        try {
            const presence = await this.getUserPresence(userId);
            return presence?.online || false;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get user's status color
     */
    getUserStatusColor(userId, presenceData = null) {
        const presence = presenceData || this.presenceData?.[userId];
        if (!presence) return '#9CA3AF';

        if (presence.online) return '#10B981';
        if (presence.status === 'away') return '#F59E0B';
        return '#9CA3AF';
    }
}

// Create singleton instance
const presenceService = new PresenceService();
export default presenceService;